// Admin Broadcast panel — Architect transmissions to newsletter subscribers.
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Radio, Send, Megaphone } from "lucide-react";
import { adminBroadcasts, adminSendCountdownTeaser, adminSendOpeningBlast, errText } from "@/lib/api";
import { goldBtnCls, fmtDate } from "@/components/admin/constants";
import { FieldAnalytics } from "@/components/admin/FieldAnalytics";

const BroadcastCard = ({ icon: Icon, kicker, title, meta, preview, subject, cta, disabled, disabledNote, onSend, sending, testId }) => (
  <div className="border border-gold/25 bg-charcoal p-6" data-testid={`${testId}-card`}>
    <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">
      <Icon size={11} className="mr-1.5 inline align-middle" /> {kicker}
    </p>
    <h3 className="mt-3 font-display text-2xl uppercase tracking-tight text-cream">{title}</h3>
    <div className="mt-3 space-y-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-silver/70">
      {meta.map(([k, v]) => <p key={k}>{k}: <span className="text-goldlight">{v}</span></p>)}
    </div>
    <div className="mt-4 border border-silver/20 bg-onyx p-4">
      <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-gold">Preview</p>
      <p className="mt-2 font-body text-sm leading-relaxed text-silver/80">{preview}</p>
      <p className="mt-2 font-mono text-[9px] uppercase tracking-[0.2em] text-silver/50">Subject: {subject}</p>
    </div>
    <button onClick={onSend} disabled={disabled || sending} data-testid={`${testId}-send-btn`}
      className={goldBtnCls + " mt-4 inline-flex items-center gap-2 px-6 py-3 text-xs"}>
      <Send size={13} /> {sending ? "Transmitting..." : cta}
    </button>
    {disabled && disabledNote && <p className="mt-3 font-mono text-[9px] uppercase tracking-[0.2em] text-silver/50">{disabledNote}</p>}
  </div>
);

export const BroadcastPanel = () => {
  const [data, setData] = useState(null);
  const [sending, setSending] = useState(null);

  const load = () => adminBroadcasts().then(setData).catch((e) => toast.error(errText(e)));
  useEffect(() => { load(); }, []);

  const fire = (key, fn, label) => async () => {
    if (!window.confirm(`Send "${label}" to ${data.subscribers} subscriber${data.subscribers === 1 ? "" : "s"} now? This cannot be undone.`)) return;
    setSending(key);
    try {
      const res = await fn();
      toast.success(`Transmission sent — ${res.sent} delivered${res.failed ? `, ${res.failed} failed` : ""}.`);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSending(null);
    }
  };

  if (!data) return <p className="mt-8 font-mono text-[10px] uppercase tracking-[0.3em] text-silver/50">Loading broadcast console...</p>;
  const noSubs = data.subscribers === 0;

  return (
    <div className="mt-6" data-testid="broadcast-panel">
      <FieldAnalytics />
      <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.25em] text-silver/70">
        Subscribers: <span className="text-goldlight" data-testid="broadcast-subscriber-count">{data.subscribers}</span> · Window opens {data.opens_label} · Closes {data.closes_label}
      </p>
      <div className="mt-4 grid gap-5 lg:grid-cols-2">
        <BroadcastCard
          icon={Radio} kicker="Countdown teaser" title="A transmission from The Architect"
          meta={[["Current countdown", `T-minus ${data.days_left} days`]]}
          preview={`"I've been watching the Village fill with talent... On ${data.opens_label}, the recruitment window unseals. Review the mission brief. Prepare your file. This transmission will not repeat."`}
          subject={`Incoming transmission: T-minus ${data.days_left} days ⭐ · CTA → /cohort`}
          cta="Send Countdown Teaser"
          disabled={noSubs || data.window_open || data.days_left <= 0}
          disabledNote={noSubs ? "No subscribers yet." : "Window already open — countdown teaser retired."}
          onSend={fire("teaser", adminSendCountdownTeaser, "Countdown Teaser")}
          sending={sending === "teaser"} testId="teaser"
        />
        <BroadcastCard
          icon={Megaphone} kicker="Opening day blast" title="The window is open"
          meta={[["Unlocks", data.opens_label], ["Applications close", data.closes_label]]}
          preview={`"The seal is broken. Student + Creative applications for the Founding Cohort 2027 are officially live. You have until ${data.closes_label}. Founding seats are limited — move like it."`}
          subject="THE WINDOW IS OPEN ⭐ Founding Cohort applications are live · CTA → /apply/student"
          cta="Send Opening Blast"
          disabled={noSubs || !data.window_open}
          disabledNote={noSubs ? "No subscribers yet." : `Locked until ${data.opens_label} — fires only while the window is open.`}
          onSend={fire("blast", adminSendOpeningBlast, "Opening Day Blast")}
          sending={sending === "blast"} testId="blast"
        />
      </div>

      <div className="mt-5 border border-silver/20 bg-charcoal/60 p-6">
        <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Transmission log</p>
        {data.history.length === 0 ? (
          <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.25em] text-silver/50" data-testid="broadcast-history-empty">No broadcasts sent yet.</p>
        ) : (
          <ul className="mt-4 space-y-3" data-testid="broadcast-history">
            {data.history.map((b) => (
              <li key={b.id} className="border border-silver/15 bg-onyx p-4">
                <p className="font-head text-sm font-extrabold uppercase tracking-wide text-cream">{b.subject}</p>
                <p className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-silver/60">
                  {fmtDate(b.created_at)} · {b.sent}/{b.total} delivered{b.failed ? ` · ${b.failed} failed` : ""}{b.days_left != null ? ` · T-minus ${b.days_left}` : ""}
                </p>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
